import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ArrowLeft, MapPin } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAttendance } from '../hooks';

export function AttendanceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { data: attendance, isLoading, isError, error } = useAttendance(id!);

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !attendance) {
    return (
      <div className="p-4">
        <p className="text-red-500">{error?.message || 'Attendance not found'}</p>
        <Link to="/app/attendances">
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToList')}
          </Button>
        </Link>
      </div>
    );
  }

  const hasLocation = attendance.latitude && attendance.longitude;

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/app/attendances">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('back')}
            </Button>
          </Link>
          <h1 className="text-2xl font-bold">
            {t('attendanceDetails')} - {attendance.createdBy?.name || `#${attendance.id}`}
          </h1>
        </div>
        {hasLocation && (
          <Button
            variant="default"
            onClick={() =>
              window.open(
                `https://www.google.com/maps?q=${attendance.latitude},${attendance.longitude}`,
                '_blank'
              )
            }
          >
            <MapPin className="h-4 w-4 mr-2" />
            {t('viewOnMap')}
          </Button>
        )}
      </div>

      {/* Attendance Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>{t('attendanceInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">{t('id')}</p>
                <p className="font-medium">{attendance.id}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('arrivedAt')}</p>
                <p className="font-medium">
                  {attendance.arrivedAt
                    ? format(new Date(attendance.arrivedAt), 'yyyy-MM-dd HH:mm')
                    : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('leavedAt')}</p>
                <p className="font-medium">
                  {attendance.leavedAt
                    ? format(new Date(attendance.leavedAt), 'yyyy-MM-dd HH:mm')
                    : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('latitude')}</p>
                <p className="font-medium">{attendance.latitude ?? '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('longitude')}</p>
                <p className="font-medium">{attendance.longitude ?? '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('reasonsInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">{t('lateReason')}</p>
                <p className="font-medium">{attendance.late_reason || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('earlyReason')}</p>
                <p className="font-medium">{attendance.early_reason || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Created By Details */}
      {attendance.createdBy && (
        <Card>
          <CardHeader>
            <CardTitle>{t('createdBy')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">{t('id')}</p>
                <p className="font-medium">{attendance.createdBy.id}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('name')}</p>
                <Link
                  to={`/app/users/${attendance.createdBy.id}`}
                  className="font-medium text-blue-700 hover:underline"
                >
                  {attendance.createdBy.name}
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* System Info */}
      <Card>
        <CardHeader>
          <CardTitle>{t('systemInfo')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">{t('createdAt')}</p>
              <p className="font-medium">
                {attendance.createdAt
                  ? format(new Date(attendance.createdAt), 'yyyy-MM-dd HH:mm')
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('updatedAt')}</p>
              <p className="font-medium">
                {attendance.updatedAt
                  ? format(new Date(attendance.updatedAt), 'yyyy-MM-dd HH:mm')
                  : '-'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
